// src/prototype/pages/EAScoringRationalizationPageApi.tsx
// ============================================================
// EA SCORING RATIONALE  (/ea/use-case/:id/rationale)
//
// Visual chrome matches Decision Brief / ROI Model. Measure rows follow
// the storefront scoring tab (pips, source chips, collapsed rationale).
// Data stays on GET /api/v2/scoring-rationale/:clientId/:useCaseId.
// ============================================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  Cog,
  Database,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  ChevronRight,
} from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { type EffortTag } from '../components/EffortTagPill';
import { EAUseCaseHubShell, type HubView } from '../components/hub/EAUseCaseHubShell';
import { PersonalizationDeltaCard } from '../components/hub/PersonalizationDeltaCard';
import { sanitizeDisplayLabel } from '@/utils/sanitizeDisplayLabel';
import {
  ensureCatalogBriefSnapshot,
  fetchOpportunities,
  isPlaceholderUseCaseTitle,
  peekCatalogBriefSnapshot,
  peekEvaluation,
  UCE_CATALOG_FILTERS,
} from '../services/useCaseExplorerService';
import {
  fetchScoringRationale,
  peekScoringRationale,
  type ScoringRationaleApiV2Data,
  type ScoringRationaleDimensionRaw,
  type ScoringRationaleResponse,
} from '../services/scoringRationaleService';
import { buildDrilldownNav, readDrilldownNav, resolveDrilldownExit } from '../utils/eaDrilldownNav';
import { EA_CLIENT_ID } from '../constants/eaClient';
import { useEA } from '../contexts/EAContext';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import {
  ILLUSTRATIVE_PERSONALIZATION_DELTA,
  ILLUSTRATIVE_PRIORITY_THEMES,
} from '../data/illustrativePersonalizationDelta';
import { verdictFromScore, type VerdictKey } from '../theme/decisionTokens';
import { formatDimensionTypeLabel } from '../utils/executiveCopy';

type SourceType = 'Survey' | 'Document' | 'Inferred' | 'Benchmark';
type WeightLabel = 'High' | 'Medium' | 'Low';

interface Factor {
  measureName: string;
  measureDescription?: string;
  rationale: string;
  weight: WeightLabel | null;
  source: SourceType;
  sourceRaw?: string;
  /** 1–5 measure score from API */
  score?: number;
}

interface Dimension {
  id: string;
  name: string;
  description: string;
  /** 0–100 display scale (from API fraction × 100 or legacy 0–100) */
  overallScore: number;
  dimensionType?: string;
  icon: React.ElementType;
  factors: Factor[];
  contributingFactorCount?: number;
}

interface PageData {
  id: string;
  title: string;
  category: string;
  rank: number;
  overallScore: number;
  overallScoreDisplay: string;
  priorityBand?: string;
  quadrant?: string;
  businessValueScore?: number;
  easeOfImplementationScore?: number;
  dimensions: Dimension[];
  dimensionCount: number;
  generatedAt?: string;
}


const inferSource = (text?: string): SourceType => {
  if (!text) return 'Inferred';
  const lower = text.toLowerCase();
  if (lower.includes('survey')) return 'Survey';
  if (lower.includes('document') || lower.includes('board') || lower.includes('materials')) return 'Document';
  if (lower.includes('benchmark') || lower.includes('peer')) return 'Benchmark';
  return 'Inferred';
};

const mapSourceType = (raw?: string | null): SourceType => {
  if (!raw) return 'Inferred';
  const t = raw.trim();
  if (t === 'Survey' || t === 'Document' || t === 'Inferred' || t === 'Benchmark') return t;
  return inferSource(t);
};

const mapWeight = (value?: string | null): WeightLabel | null => {
  if (value == null || String(value).trim() === '') return null;
  const lower = String(value).toLowerCase();
  if (lower === 'high') return 'High';
  if (lower === 'medium') return 'Medium';
  if (lower === 'low') return 'Low';
  return null;
};

/** API sometimes embeds JSON in rationalization strings */
const parseRationale = (raw?: string | null): string => {
  if (raw == null || raw === '') return 'Rationale not available';
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as { rationalization?: string };
      if (typeof parsed.rationalization === 'string' && parsed.rationalization.trim()) {
        return parsed.rationalization.trim();
      }
    } catch {
      /* use raw */
    }
  }
  return raw;
};

function normalizeScoringPayload(body: ScoringRationaleResponse): {
  header: ScoringRationaleApiV2Data['header'];
  scoring_dimensions: ScoringRationaleDimensionRaw[];
  use_case_name?: string;
  use_case_category?: string;
  overall_score?: number | null;
  scores?: ScoringRationaleResponse['scores'];
  meta?: ScoringRationaleResponse['meta'];
  generated_at?: string;
} {
  const nested = body.data;
  if (
    nested != null &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    (nested.header != null || Array.isArray(nested.scoring_dimensions))
  ) {
    const h = nested.header;
    return {
      header: h,
      scoring_dimensions: Array.isArray(nested.scoring_dimensions) ? nested.scoring_dimensions : [],
      use_case_name:
        body.use_case_name ||
        h?.useCaseName ||
        h?.use_case_name ||
        undefined,
      use_case_category:
        body.use_case_category ||
        h?.useCaseCategory ||
        h?.use_case_category ||
        undefined,
      overall_score: body.overall_score,
      scores: body.scores,
      meta: body.meta,
      generated_at: body.generated_at,
    };
  }
  return {
    header: undefined,
    scoring_dimensions: Array.isArray(body.scoring_dimensions) ? body.scoring_dimensions : [],
    use_case_name: body.use_case_name,
    use_case_category: body.use_case_category,
    overall_score: body.overall_score,
    scores: body.scores,
    meta: body.meta,
    generated_at: body.generated_at,
  };
}

const getDimensionIcon = (name: string, dimensionType?: string): React.ElementType => {
  const dt = (dimensionType || '').toLowerCase();
  if (dt === 'eoi') return Cog;
  if (dt === 'value') return TrendingUp;
  const key = name.toLowerCase();
  if (key.includes('strategic') || key.includes('alignment')) return Target;
  if (key.includes('revenue') || key.includes('cost') || key.includes('financial')) return TrendingUp;
  if (key.includes('time') && key.includes('value')) return TrendingUp;
  if (key.includes('organizational') || key.includes('readiness') || key.includes('implement')) return Cog;
  if (key.includes('data')) return Database;
  if (key.includes('regulatory') || key.includes('compliance') || key.includes('risk')) return ShieldAlert;
  return Target;
};

const formatLabel = (raw?: string) => {
  if (!raw) return '';
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

/**
 * DMD-1166: Derive effort tag from ease-of-implementation score using the
 * same thresholds as useCaseExplorerService.normalizeUCEOpportunity so badges
 * stay consistent across Decision Brief / ROI Model / Scoring Rationale.
 */
const EFFORT_BY_EOI = (eoi: number | null | undefined): EffortTag | null => {
  if (eoi == null || !Number.isFinite(eoi)) return null;
  if (eoi >= 60) return 'Quick Win';
  if (eoi >= 40) return 'Moderate';
  return 'High';
};

type PriorityLabel = 'Critical' | 'High' | 'Medium' | 'Low';

/**
 * DMD-1166: Prefer API `priorityBand` (per–use-case in client_use_case_scores).
 * Deriving from business-value with a 55 cut collapsed almost every ranked
 * opportunity to "High" — which is what QA reported after the first fix.
 */
const priorityFromBand = (band: string | null | undefined): PriorityLabel | null => {
  if (!band) return null;
  const key = String(band).trim().toLowerCase().replace(/[_\s]+/g, '');
  if (key === 'critical' || key === 'crit') return 'Critical';
  if (key === 'high') return 'High';
  if (key === 'medium' || key === 'med' || key === 'moderate') return 'Medium';
  if (key === 'low') return 'Low';
  return null;
};

/** Last-resort priority from BV when the API omits priorityBand. */
const priorityFromValueScore = (bv: number | null | undefined): PriorityLabel | null => {
  if (bv == null || !Number.isFinite(bv)) return null;
  if (bv >= 70) return 'High';
  if (bv >= 55) return 'Medium';
  return 'Low';
};

const SOURCE_LABEL: Record<string, string> = {
  llm: 'Model-evaluated',
  llm_generated: 'Model-evaluated',
  survey: 'Client survey',
  document: 'Client document',
  benchmark: 'Benchmark',
  inferred: 'Inferred',
};

function humanizeSource(raw: string): string {
  const s = raw.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sourceChipLabel(raw?: string, mapped?: SourceType): string {
  if (raw) {
    const hit = SOURCE_LABEL[raw.toLowerCase()];
    if (hit) return hit;
    if (!SOURCE_LABEL[raw.toLowerCase()] && raw.length > 1) return humanizeSource(raw);
  }
  return mapped || 'Inferred';
}

const WEIGHT_CLS: Record<WeightLabel, string> = {
  High: 'bg-[#18365E] text-white',
  Medium: 'bg-[#dbe3ee] text-[#18365E]',
  Low: 'bg-[#F5F7FA] text-[#717182] border border-[#e5e7eb]',
};

const scoreCls = (s: number) =>
  s >= 75 ? 'bg-[#18365E] text-white' : s >= 55 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800';

function firstSentence(text: string): string {
  const m = text.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return (m ? m[0] : text).trim();
}

function ScorePips({ value }: { value: number }) {
  const filled = Math.round(value);
  return (
    <span className="inline-flex items-center gap-1" title={`Score ${value} of 5`}>
      <span className="flex items-center gap-[3px]">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={`w-[7px] h-[7px] rounded-full ${i <= filled ? 'bg-[#18365E]' : 'bg-[#dbe3ee]'}`}
          />
        ))}
      </span>
      <span className="text-[10px] font-bold text-[#18365E]">
        {value.toFixed(1)}
        <span className="text-[#717182] font-semibold">/5</span>
      </span>
    </span>
  );
}

function MeasureRow({ factor }: { factor: Factor }) {
  const [showFull, setShowFull] = useState(false);
  const [showEv, setShowEv] = useState(false);
  const lead = firstSentence(factor.rationale);
  const hasMore = factor.rationale.length > lead.length + 1;
  const evaluated = factor.score != null || (factor.rationale && factor.rationale !== 'Rationale not available');

  if (!evaluated) {
    return (
      <div className="bg-white rounded-lg border border-dashed border-[#e5e7eb] p-3.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-[#717182]">{factor.measureName}</span>
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#F5F7FA] border border-[#e5e7eb] text-[#717182]">
            Not evaluated
          </span>
        </div>
        <p className="text-xs text-[#717182] leading-relaxed mt-1.5 italic">
          Not yet evaluated for this use case.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] p-3.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-[#18365E]">{factor.measureName}</span>
        {factor.score != null && <ScorePips value={factor.score} />}
        {factor.weight && (
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${WEIGHT_CLS[factor.weight]}`}>
            {factor.weight} weight
          </span>
        )}
        <span
          className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-white border border-[#e5e7eb] text-[#717182]"
          title={factor.sourceRaw ? `source_type: ${factor.sourceRaw}` : undefined}
        >
          {sourceChipLabel(factor.sourceRaw, factor.source)}
        </span>
      </div>
      {factor.measureDescription && (
        <p className="text-[10px] text-[#717182] mt-1 leading-snug">{factor.measureDescription}</p>
      )}
      <p className="text-xs text-[#4b5563] leading-relaxed mt-1.5">
        {showFull || !hasMore ? factor.rationale : lead}
      </p>
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowFull((v) => !v)}
          className="mt-1 text-[10px] font-bold text-[#18365E] hover:text-[#FF6E4C]"
        >
          {showFull ? 'Show less' : 'Show full rationale'}
        </button>
      )}
      {factor.measureDescription && (
        <>
          <button
            type="button"
            onClick={() => setShowEv((v) => !v)}
            className="mt-2 text-[10px] font-bold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1"
          >
            {showEv ? <ChevronUp className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {showEv ? 'Hide evidence' : 'Show evidence'}
          </button>
          {showEv && (
            <div className="mt-2 bg-white border border-[#e5e7eb] rounded-md px-3 py-2">
              <p className="text-[11px] text-[#4b5563] leading-relaxed italic">
                &ldquo;{factor.measureDescription}&rdquo;
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function toDimensionDisplayScore(dimensionScore: number | null | undefined): number {
  const n = Number(dimensionScore);
  if (!Number.isFinite(n)) return 0;
  if (n >= 0 && n <= 1) return Math.round(n * 100);
  return Math.round(n);
}

const mapResponse = (
  id: string,
  payload: ScoringRationaleResponse,
  opportunity?: { name?: string; category_name?: string; category?: string } | null,
): PageData => {
  const norm = normalizeScoringPayload(payload);
  const h = norm.header;

  const overallRaw = h?.overallScore ?? norm.overall_score ?? norm.scores?.total_score ?? 0;
  const overallNum = Number(overallRaw);
  const overallScoreDisplay = Number.isFinite(overallNum)
    ? Math.abs(overallNum - Math.round(overallNum)) < 1e-6
      ? String(Math.round(overallNum))
      : overallNum.toFixed(1).replace(/\.0$/, '')
    : '0';

  const dimensions: Dimension[] = norm.scoring_dimensions.map((dimension) => ({
    id: String(dimension.category_id),
    name: sanitizeDisplayLabel(dimension.category_name) || dimension.category_name,
    description: sanitizeDisplayLabel(dimension.description) || dimension.description || 'No description available.',
    overallScore: toDimensionDisplayScore(dimension.dimension_score),
    dimensionType: dimension.dimension_type,
    icon: getDimensionIcon(dimension.category_name, dimension.dimension_type),
    contributingFactorCount: dimension.contributing_factor_count,
    factors: (dimension.contributing_factors || []).map((factor) => ({
      measureName: sanitizeDisplayLabel(factor.measure_name) || factor.measure_name || 'Unnamed factor',
      measureDescription: factor.description
        ? sanitizeDisplayLabel(factor.description) || undefined
        : undefined,
      rationale: parseRationale(factor.rationalization),
      weight: mapWeight(factor.weight_label),
      source: mapSourceType(factor.source_type),
      sourceRaw: factor.source_type || undefined,
      score:
        factor.score == null || !Number.isFinite(Number(factor.score))
          ? undefined
          : Math.round(Number(factor.score)),
    })),
  }));

  const pickLabel = (...candidates: Array<string | null | undefined>) => {
    for (const c of candidates) {
      const trimmed = c != null ? String(c).trim() : '';
      if (!trimmed) continue;
      if (trimmed.toLowerCase() === 'uncategorized') continue;
      if (isPlaceholderUseCaseTitle(trimmed)) continue;
      const cleaned = sanitizeDisplayLabel(trimmed) || trimmed;
      if (cleaned && !isPlaceholderUseCaseTitle(cleaned)) return cleaned;
    }
    return null;
  };

  const title =
    pickLabel(norm.use_case_name, opportunity?.name, peekCatalogBriefSnapshot(EA_CLIENT_ID, id)?.title) ||
    `Use Case ${id}`;
  const category =
    pickLabel(
      norm.use_case_category,
      opportunity?.category_name,
      opportunity?.category,
    ) || 'Uncategorized';

  return {
    id,
    title,
    category,
    rank: h?.rankBadge ?? norm.scores?.rank ?? 0,
    overallScore: Number.isFinite(overallNum) ? overallNum : 0,
    overallScoreDisplay,
    priorityBand: h?.priorityBand,
    quadrant: h?.quadrant,
    businessValueScore: h?.businessValueScore,
    easeOfImplementationScore: h?.easeOfImplementationScore,
    dimensions,
    dimensionCount: norm.meta?.dimension_count ?? dimensions.length,
    generatedAt: norm.generated_at,
  };
};

const DimensionCard: React.FC<{
  dimension: Dimension;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ dimension, isExpanded, onToggle }) => {
  const Icon = dimension.icon;
  const factorCount = dimension.contributingFactorCount ?? dimension.factors.length;
  const evaluatedCount = dimension.factors.filter(
    (f) => f.score != null || (f.rationale && f.rationale !== 'Rationale not available'),
  ).length;

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left group"
      >
        <span className="w-8 h-8 rounded-lg bg-[#F5F7FA] border border-[#e5e7eb] text-[#18365E] flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="block text-sm font-bold text-[#18365E]">{dimension.name}</span>
            {dimension.dimensionType && (
              <span className="text-[9px] font-semibold uppercase tracking-wide text-[#18365E]/80 bg-[#18365E]/10 px-1.5 py-0.5 rounded">
                {formatDimensionTypeLabel(dimension.dimensionType) ?? dimension.dimensionType}
              </span>
            )}
          </span>
          <span className="block text-[10px] text-[#717182]">
            {evaluatedCount === factorCount
              ? `${factorCount} scored measures`
              : `${evaluatedCount} of ${factorCount} measures evaluated`}
          </span>
        </span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg shrink-0 ${scoreCls(dimension.overallScore)}`}>
          {dimension.overallScore.toFixed(1)}
        </span>
        <span className="text-[#717182] group-hover:text-[#FF6E4C]">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {isExpanded && (
        <div className="px-5 pb-5">
          <p className="text-xs text-[#717182] leading-relaxed mb-3">{dimension.description}</p>
          <div className="space-y-2.5">
            {dimension.factors.map((factor, idx) => (
              <MeasureRow key={`${dimension.id}-${idx}`} factor={factor} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function EAScoringRationalizationPageApi({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = EA_CLIENT_ID;
  const { finalistIds, addFinalist } = useEA();
  const { isFreemium } = useDemoPlan();
  const navState = readDrilldownNav(location.state);
  const navSnapshot =
    navState.briefSnapshot && String(navState.briefSnapshot.id) === String(id)
      ? navState.briefSnapshot
      : null;

  const cachedScoring = id ? peekScoringRationale(clientId, id) : null;
  const cachedEval = id ? peekEvaluation(clientId, id) : null;
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
  const [data, setData] = useState<PageData | null>(() =>
    id && cachedScoring
      ? mapResponse(id, cachedScoring, navSnapshot
        ? { name: navSnapshot.title, category_name: navSnapshot.category }
        : null)
      : null,
  );
  const [loading, setLoading] = useState(() => !cachedScoring);
  const [error, setError] = useState<string | null>(null);
  const [rankedIds, setRankedIds] = useState<string[]>(() =>
    Array.isArray(navState.rankedIds) ? navState.rankedIds.map(String) : [],
  );
  const [catalogTotal, setCatalogTotal] = useState(() =>
    typeof navState.catalogTotal === 'number' ? navState.catalogTotal : 0,
  );

  useEffect(() => {
    if (embedded) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id, embedded]);

  useEffect(() => {
    if (rankedIds.length > 0) return;
    fetchOpportunities(String(clientId), UCE_CATALOG_FILTERS)
      .then((res) => {
        setRankedIds((res.data ?? []).map((o) => String(o.use_case_id)));
        setCatalogTotal(res.pagination?.total ?? res.data?.length ?? 0);
      })
      .catch(() => {
        /* rank pager is optional */
      });
  }, [clientId, rankedIds.length]);

  useEffect(() => {
    if (!id) return;
    const parsedId = Number(id);
    if (!Number.isFinite(parsedId)) {
      setData({
        id,
        title: `Use Case ${id}`,
        category: 'Uncategorized',
        rank: 0,
        overallScore: 0,
        overallScoreDisplay: '0',
        dimensions: [],
        dimensionCount: 0,
      });
      setLoading(false);
      setError(null);
      return;
    }

    const applyPayload = (payload: ScoringRationaleResponse) => {
      const catalogSnap = peekCatalogBriefSnapshot(clientId, id);
      const mapped = mapResponse(id, payload, {
        name:
          (navSnapshot?.title && !isPlaceholderUseCaseTitle(navSnapshot.title)
            ? navSnapshot.title
            : null) ||
          catalogSnap?.title ||
          undefined,
        category_name: navSnapshot?.category || catalogSnap?.category || undefined,
      });
      setData(mapped);
      if (mapped.dimensions[0]) {
        setExpandedDimensions(new Set([mapped.dimensions[0].id]));
      }
      setError(null);
      setLoading(false);

      if (mapped.title && !isPlaceholderUseCaseTitle(mapped.title)) return;
      void ensureCatalogBriefSnapshot(clientId, id).then((snap) => {
        if (!snap?.title) return;
        setData((prev) =>
          prev && (!prev.title || isPlaceholderUseCaseTitle(prev.title))
            ? { ...prev, title: snap.title, category: prev.category || snap.category || prev.category }
            : prev,
        );
      });
    };

    const cached = peekScoringRationale(clientId, id);
    if (cached) {
      applyPayload(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Scoring payload already includes name/category/rank — no parallel opportunity fetch.
    fetchScoringRationale(clientId, id)
      .then((payload) => {
        if (cancelled) return;
        applyPayload(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load scoring rationale');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, id, navSnapshot?.title, navSnapshot?.category]);

  const restoreState = navState.restore;
  // DMD-1186: ← back always exits to All Opportunities / Execution — not Decision Brief / ROI.
  const exitNav = resolveDrilldownExit(navState);
  const fromStep3 = exitNav.fromActivate;
  const backLabel = exitNav.label;
  const backPath = exitNav.path;

  const idx = rankedIds.indexOf(String(id || ''));
  const prevId = idx > 0 ? rankedIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rankedIds.length - 1 ? rankedIds[idx + 1] : null;
  const pageTo = (targetId: string | null) => {
    if (!targetId) return;
    void (async () => {
      const catalogSnap =
        peekCatalogBriefSnapshot(clientId, targetId) ||
        (await ensureCatalogBriefSnapshot(clientId, targetId));
      navigate(`/ea/use-case/${targetId}/rationale`, {
        state: buildDrilldownNav(navState, {
          rankedIds,
          catalogTotal,
          briefSnapshot: catalogSnap,
        }),
      });
    })();
  };
  const drilldownState = buildDrilldownNav(navState, {
    rankedIds,
    catalogTotal,
    briefSnapshot: id
      ? {
          id: String(id),
          title: data?.title || navSnapshot?.title || '',
          category: data?.category || navSnapshot?.category || '',
          totalScore: data?.overallScore,
          valueBand: navSnapshot?.valueBand,
        }
      : undefined,
  });

  const handleBack = () => {
    navigate(backPath, {
      state: {
        restore: restoreState,
        ...(fromStep3 ? {} : { returnToTab: 'explore' }),
      },
    });
  };
  const rank = data?.rank || 0;
  const portfolioSize = catalogTotal || rankedIds.length || 0;
  const inPortfolio = Boolean(id) && finalistIds.has(String(id));
  const title =
    data?.title ||
    navSnapshot?.title ||
    (id ? peekCatalogBriefSnapshot(clientId, id)?.title : null) ||
    'Scoring rationale';
  const category = data?.category || navSnapshot?.category || '';
  const verdict: VerdictKey = verdictFromScore(data?.overallScore ?? navSnapshot?.totalScore ?? 0);

  const evalHeader = cachedEval?.data?.header || cachedEval?.data?.use_case || null;
  const kpiAnnual =
    evalHeader?.roi_range ||
    (typeof evalHeader?.annual_benefit_m === 'number'
      ? `$${Number(evalHeader.annual_benefit_m).toFixed(1)}M`
      : '—');
  const kpiNpv =
    typeof evalHeader?.npv_3yr_m === 'number'
      ? `$${Number(evalHeader.npv_3yr_m).toFixed(1)}M`
      : '—';
  const kpiTtv = evalHeader?.time_to_roi || evalHeader?.time_to_value || '—';

  const handleHubTab = (view: HubView) => {
    const paths: Record<HubView, string> = {
      brief: `/ea/use-case/${String(id || '')}/brief`,
      rationale: `/ea/use-case/${String(id || '')}/rationale`,
      roi: `/ea/use-case/${String(id || '')}/roi`,
    };
    if (view === 'rationale') return;
    navigate(paths[view], {
      state: buildDrilldownNav(drilldownState, {
        restore: restoreState ?? drilldownState?.restore,
      }),
    });
  };

  const handleProceed = () => {
    if (!id) return;
    if (isFreemium) {
      navigate('/ea/upgrade');
      return;
    }
    if (!inPortfolio) addFinalist(id);
  };

  const handleOpenCommandCenter = () => {
    if (!id) return;
    navigate(`/ea/execute/${id}`, {
      state: buildDrilldownNav(drilldownState, {
        restore: restoreState ?? drilldownState?.restore,
      }),
    });
  };

  const toggleDimension = (dimensionId: string) => {
    setExpandedDimensions((prev) => {
      const next = new Set(prev);
      if (next.has(dimensionId)) next.delete(dimensionId);
      else next.add(dimensionId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedDimensions(new Set((data?.dimensions || []).map((d) => d.id)));
  };

  const collapseAll = () => {
    setExpandedDimensions(new Set());
  };

  const handleShare = () => {
    void navigator.clipboard?.writeText(window.location.href);
  };

  const allFactors = data?.dimensions.flatMap((d) => d.factors) ?? [];
  const evaluatedCount = allFactors.filter(
    (f) => f.score != null || (f.rationale && f.rationale !== 'Rationale not available'),
  ).length;

  // DMD-1166: Effort from EOI (Quick Win / Moderate / High). Priority from API
  // priorityBand (varies per use case). Do NOT show matrix "Quick Wins" here —
  // QA treated that quadrant label as Effort and it looked identical for most UCs.
  const derivedEffortTag = EFFORT_BY_EOI(data?.easeOfImplementationScore);
  const hasNoDimensions = !loading && (data?.dimensions.length || 0) === 0;
  const showPersonalizationDelta =
    data?.businessValueScore != null || data?.easeOfImplementationScore != null;

  const rationaleBody = (
    <>
        {loading && (
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
            <div className="w-8 h-8 border-2 border-[#18365E]/20 border-t-[#18365E] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium text-[#18365E]">Loading scoring rationale...</p>
          </div>
        )}

        {error && (
          <div className="px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 mb-3">
            Unable to load live scoring rationale. {error}
          </div>
        )}

        {hasNoDimensions && (
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
            <p className="text-sm font-medium text-[#18365E]">
              Scoring rationale is not yet available for this use case.
            </p>
          </div>
        )}

        {!loading && (data?.dimensions.length || 0) > 0 && (
          <div className="space-y-3">
            {showPersonalizationDelta && (
              <PersonalizationDeltaCard
                illustrative
                delta={{ ...ILLUSTRATIVE_PERSONALIZATION_DELTA }}
                themes={[...ILLUSTRATIVE_PRIORITY_THEMES]}
              />
            )}

            <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 flex items-center gap-4 flex-wrap">
              <div className="text-center shrink-0 bg-[#18365E] text-white rounded-xl px-5 py-2.5">
                <p className="text-[9px] font-semibold uppercase tracking-widest text-white/60">Composite</p>
                <p className="text-xl font-bold leading-tight tabular-nums">
                  {data?.overallScoreDisplay ?? '—'}
                </p>
              </div>
              <div className="flex-1 min-w-[220px]">
                <p className="text-xs text-[#717182] leading-relaxed">
                  Scored across {data?.dimensionCount ?? data?.dimensions.length ?? 0} dimensions; each aggregates
                  individually scored measures with their own rationale.
                </p>
                {allFactors.length > 0 && (
                  <p className="text-[11px] text-[#717182] leading-relaxed mt-1">
                    <span className="font-semibold text-[#18365E]">
                      {evaluatedCount} of {allFactors.length} measures evaluated
                    </span>
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button type="button" onClick={expandAll} className="text-[11px] font-semibold text-[#18365E] hover:text-[#FF6E4C]">
                  Expand all
                </button>
                <span className="text-[#e5e7eb]">·</span>
                <button type="button" onClick={collapseAll} className="text-[11px] font-semibold text-[#18365E] hover:text-[#FF6E4C]">
                  Collapse all
                </button>
              </div>
            </div>

            {(data?.dimensions || []).map((dimension) => (
              <DimensionCard
                key={dimension.id}
                dimension={dimension}
                isExpanded={expandedDimensions.has(dimension.id)}
                onToggle={() => toggleDimension(dimension.id)}
              />
            ))}
          </div>
        )}
    </>
  );

  if (embedded) {
    return rationaleBody;
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-1"> {/* DMD-1168: tighter wrap */}
        <EAJourneyBar />
      </div>

      <EAUseCaseHubShell
        activeView="rationale"
        title={title}
        category={category}
        rank={rank}
        portfolioSize={portfolioSize}
        effortTag={derivedEffortTag}
        inPortfolio={inPortfolio}
        verdict={verdict}
        decisionContent={
          <span>
            {verdict === 'green'
              ? 'Proceed: scores and strategic fit support carrying this initiative forward.'
              : verdict === 'cond'
                ? 'Validate first: confirm key assumptions before committing implementation capacity.'
                : 'Hold: revisit later unless constraints or scores improve.'}
          </span>
        }
        kpis={{
          annualBenefit: kpiAnnual,
          npv: kpiNpv,
          timeToValue: kpiTtv,
          compositeScore:
            data?.overallScoreDisplay != null
              ? `${data.overallScoreDisplay} / 100`
              : data?.overallScore != null
                ? `${Math.round(data.overallScore)} / 100`
                : '—',
          portfolioRank: rank ? `#${rank} of ${portfolioSize || '—'}` : '—',
        }}
        freshnessLabel={
          data?.generatedAt
            ? `Updated ${new Date(data.generatedAt).toLocaleString()}`
            : 'Live-computed'
        }
        backLabel={backLabel}
        onBack={handleBack}
        prevId={prevId}
        nextId={nextId}
        onPage={pageTo}
        onTab={handleHubTab}
        onShare={handleShare}
        onProceed={handleProceed}
        onOpenCommandCenter={handleOpenCommandCenter}
        maxWidthClass="max-w-6xl"
      >
        {rationaleBody}
      </EAUseCaseHubShell>
    </div>
  );
}
